import React, { Fragment, useEffect, useState } from "react";
import api from "../../api";
import { formatDateDDMMYYYY } from "../../util/formatDate";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import CustomCombobox from "../../components/CustomCombobox";
import Badge from "../../components/Badge";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
    ReportShell,
    ReportStatCard,
    ReportPanel,
} from "../../components/reports/ReportShell";
import {
    ReportDateFilterMenu,
    ReportComboboxFilterMenu,
} from "../../components/reports/ReportFilters";

ChartJS.register(
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    ArcElement,
);

const options = [
    { name: "Datos generales", href: "/reports/general-data-report" },
    { name: "Cirugias", href: "/reports/surgery-report" },
    { name: "Estudios", href: "/reports/study-report" },
    { name: "Medicamentos", href: "#" },
];

const colors = [
    "#0ea5e9",
    "#14b8a6",
    "#f97316",
    "#fb7185",
    "#8b5cf6",
    "#facc15",
    "#38bdf8",
    "#34d399",
];

export default function MedicationPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const [loading, setLoading] = useState(true);
    const [medication, setMedication] = useState();
    const [data, setData] = useState();
    const [filters, setFilters] = useState({});
    const [fromDate, setFromDate] = useState();
    const [toDate, setToDate] = useState();
    const [medicationFilter, setMedicationFilter] = useState();
    const isDesktop = useMediaQuery("(min-width: 1024px)");

    const handleFromDate = (field, value) => setFromDate(value);
    const handleToDate = (field, value) => setToDate(value);
    const handleMedicationFilter = (field, value) => setMedicationFilter(value);

    useEffect(() => {
        if (isDesktop) setSidebarOpen(false);
    }, [isDesktop]);

    useEffect(() => {
        api.medicines.getMulti().then((response) => setMedication(response));
    }, []);

    useEffect(() => {
        setLoading(true);
        const aux = new URLSearchParams();

        Object.keys(filters || {}).forEach((prop) => {
            const value = filters[prop];
            if (Array.isArray(value)) {
                value.forEach((element) => aux.append(prop, element.id));
            } else if (value !== undefined && value !== null) {
                aux.append(prop, value);
            }
        });

        const request =
            aux.toString().length > 0
                ? api.medicationReport.getReports(aux)
                : api.medicationReport.getReports();

        request
            .then((response) => setData(response))
            .finally(() => setLoading(false));
    }, [filters]);

    const saveFromDate = () => {
        if (fromDate) setFilters((prev) => ({ ...prev, date_after: fromDate }));
    };

    const saveToDate = () => {
        if (toDate) setFilters((prev) => ({ ...prev, date_before: toDate }));
    };

    const saveMedicationFilter = () => {
        if (medicationFilter?.length) {
            setFilters((prev) => ({ ...prev, medicine: medicationFilter }));
        }
    };
    const applyDateFilters = () => {
        saveToDate();
        saveFromDate();
    };

    const deleteFromDate = () => {
        const state = { ...filters };
        delete state.date_after;
        setFilters(state);
        setFromDate(undefined);
    };

    const deleteToDate = () => {
        const state = { ...filters };
        delete state.date_before;
        setFilters(state);
        setToDate(undefined);
    };

    const deleteDates = () => {
        const state = { ...filters };
        delete state.date_after;
        delete state.date_before;
        setFilters(state);
        setFromDate(undefined);
        setToDate(undefined);
    };

    const deleteAllMedicationFilter = () => {
        const state = { ...filters };
        delete state.medicine;
        setFilters(state);
        setMedicationFilter(undefined);
    };

    const deleteMedicationFilter = (value) => {
        setFilters((prev) => ({
            ...prev,
            medicine: (prev.medicine || []).filter((element) => element.id !== value),
        }));
        setMedicationFilter((prev) =>
            (prev || []).filter((element) => element.id !== value),
        );
    };

    const chartData = data
        ? {
              labels: data.medications
                  .filter((element) => element.quantity > 5)
                  .map((element) => element.name),
              datasets: [
                  {
                      data: data.medications
                          .filter((element) => element.quantity > 5)
                          .map((element) => element.quantity),
                      backgroundColor: colors,
                  },
              ],
          }
        : undefined;

    const topMedication = data?.medications?.[0];
    const totalPrescribed = data?.totalPrescribedMedications || 0;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: isDesktop ? "left" : "bottom",
            },
        },
    };

    return (
        <ReportShell
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            setSidebarOpenDesktop={setSidebarOpenDesktop}
            isDesktop={isDesktop}
            loading={loading}
            currentLabel="Medicamentos"
            options={options}
            title="Patron de medicamentos recetados"
            actions={
                <>
                    <ReportDateFilterMenu
                        handleFromDate={handleFromDate}
                        handleToDate={handleToDate}
                        onClear={deleteDates}
                        onApply={applyDateFilters}
                    />
                    <ReportComboboxFilterMenu
                        label="Medicamentos"
                        comboboxLabel="Medicamentos"
                        options={medication}
                        handleChange={handleMedicationFilter}
                        name="medicine"
                        onClear={deleteAllMedicationFilter}
                        onApply={saveMedicationFilter}
                    />
                </>
            }
            badges={
                <>
                    {filters.date_after && <Badge text={`Desde: ${formatDateDDMMYYYY(filters.date_after)}`} handleDelete={deleteFromDate} />}
                    {filters.date_before && <Badge text={`Hasta: ${formatDateDDMMYYYY(filters.date_before)}`} handleDelete={deleteToDate} />}
                    {(filters.medicine || []).map((element) => (
                        <Badge key={element.id} text={element.name} handleDelete={() => deleteMedicationFilter(element.id)} />
                    ))}
                </>
            }
            summary={[
                <ReportStatCard key="total" label="Medicamentos recetados" value={totalPrescribed} tone="violet" />,
                <ReportStatCard key="top" label="Medicamento lider" value={topMedication?.name || "--"} tone="blue" />,
                <ReportStatCard key="count" label="Frecuencia del lider" value={topMedication?.quantity || 0} tone="emerald" />,
                <ReportStatCard
                    key="visible"
                    label="Medicamentos destacados"
                    value={data?.medications?.filter((element) => element.quantity > 5).length || 0}
                    tone="amber"
                />,
            ]}
        >
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
                <ReportPanel title="Medicamentos">
                    {chartData ? (
                        <div className="h-[340px] sm:h-[500px]">
                            <Pie options={chartOptions} data={chartData} />
                        </div>
                    ) : null}
                </ReportPanel>

                <ReportPanel title="Resumen">
                    <div className="space-y-4">
                        <ReportStatCard label="Total prescrito" value={totalPrescribed} tone="violet" />
                        <ReportStatCard label="Medicamento principal" value={topMedication?.name || "--"} tone="blue" />
                        <ReportStatCard label="Cantidad del principal" value={topMedication?.quantity || 0} tone="emerald" />
                    </div>
                </ReportPanel>
            </div>
        </ReportShell>
    );
}
